/** @file Maps an Inspector target to its view: hand-built forms where they exist, generated forms otherwise. */
import type { ReactNode } from 'react';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import type { KeyRef } from '../../stores/types';
import { entityName } from '../../forms/entityName';
import { BoneForm } from './BoneForm';
import { EntityForms } from './EntityForms';
import { formKindOf, type FormKind } from './formKindOf';
import type { InspectorTarget } from './inspectorTarget';
import { KeyframeForm } from './KeyframeForm';
import { MeshForm } from './MeshForm';

const PROJECT: FormKind = 'project';

/** The channels the keys sit on, without frames: moving a key keeps its form (and its status), another channel's key gets a fresh one. */
const keysKey = (keys: readonly KeyRef[]): string => `keys:${keys.map((k) => `${k.animation}/${k.target}/${k.channel}`).join('|')}`;

/** The view of `target` (exhaustive over InspectorTarget); kinds other areas edit show their name only (W3-41). Each view is keyed by its target, so form status never carries over to the next entity. */
export function inspectorView(target: InspectorTarget, doc: Doc, t: Translate): ReactNode {
  switch (target.kind) {
    case 'project':
      return <EntityForms key="project" doc={doc} kind={PROJECT} id="" t={t} />;
    case 'keys':
      return <KeyframeForm key={keysKey(target.keys)} doc={doc} keys={target.keys} t={t} />;
    case 'vertices':
      return <MeshForm key={`attachment:${target.attachment}`} doc={doc} id={target.attachment} t={t} vertices={target.vertices.length} />;
    case 'entity': {
      const key = `${target.entity}:${target.id}`;
      if (target.entity === 'bone') return <BoneForm key={key} doc={doc} id={target.id} t={t} />;
      const kind = formKindOf(doc, target.entity, target.id);
      if (kind === 'mesh') return <MeshForm key={key} doc={doc} id={target.id} t={t} />;
      return kind === null ? <p className="p2-inspector-note">{entityName(doc, target.entity, target.id)}</p> : <EntityForms key={key} doc={doc} kind={kind} id={target.id} t={t} />;
    }
  }
}
